import * as React from 'react'
import { AlertTriangle } from 'lucide-react'

interface ConfirmDialogProps {
  open: boolean
  title: string
  message: React.ReactNode
  confirmText?: string
  cancelText?: string
  confirmColor?: 'amber' | 'red' | 'blue'
  loading?: boolean
  onConfirm: () => void
  onCancel: () => void
}

const CONFIRM_COLOR_CLASSES: Record<string, string> = {
  amber: 'bg-amber-500 hover:bg-amber-600',
  red: 'bg-red-600 hover:bg-red-700',
  blue: 'bg-blue-600 hover:bg-blue-700',
}

/** 通用確認視窗，取代瀏覽器原生 window.confirm/alert */
export function ConfirmDialog({
  open, title, message, confirmText = '確認', cancelText = '取消',
  confirmColor = 'amber', loading = false, onConfirm, onCancel,
}: ConfirmDialogProps) {
  if (!open) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1 pt-1">
            <h3 className="font-bold text-gray-900 dark:text-white">{title}</h3>
            <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">{message}</div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 rounded-lg text-sm font-bold text-white transition-colors cursor-pointer disabled:opacity-50 ${CONFIRM_COLOR_CLASSES[confirmColor]}`}
          >
            {loading ? '處理中...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  )
}
